import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Input, Spin, Empty, message } from 'antd';
import { SendOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { proformaApi } from './proformaApi';
import {
  normalizeEntries, isComment, isPayment, activityActorLabel, activityInitials,
  entryPrimaryText, entryRemark
} from './proformaActivityHelpers';

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}

// Absolute time, tabular + compact: "06 Jul 2026, 3:42 PM". Uses the viewer's
// locale/timezone (the ISO stamp is UTC); a title carries the full stamp.
function fmtTime(at) {
  const d = dayjs(at);
  return d.isValid() ? d.format('DD MMM YYYY, h:mm A') : '';
}

// ── One timeline entry ──────────────────────────────────────────────
// Comments read as a titled note; system events read as a quiet log line;
// payment activity reads as a money line with a subtle accent.
function ActivityItem({ entry }) {
  const comment = isComment(entry);
  const payment = isPayment(entry);
  const actor = activityActorLabel(entry);
  const primary = entryPrimaryText(entry);
  const remark = entryRemark(entry);
  const cls = comment ? '' : (payment ? ' is-event is-payment' : ' is-event');
  return (
    <li className={`pf-act-item${cls}`}>
      <div className="pf-act-avatar" aria-hidden="true">{activityInitials(entry)}</div>
      <div className="pf-act-main">
        <div className="pf-act-meta">
          <span className="pf-act-actor">{actor}</span>
          <span className="pf-act-time" title={entry && entry.at ? String(entry.at) : ''}>{fmtTime(entry && entry.at)}</span>
        </div>
        {comment ? (
          <div className="pf-act-body">{primary}</div>
        ) : (
          <div className="pf-act-event">
            {primary}
            {remark ? <span className="pf-act-reason"> — “{remark}”</span> : null}
          </div>
        )}
      </div>
    </li>
  );
}

// ── Activity tab — unified timeline + comment composer ──────────────
// Fetches the merged event/comment feed for one proforma, shows it
// newest-first, and lets the user append an immutable comment. The drawer
// mounts this fresh per open (destroyOnHidden), so a plain mount-fetch is safe.
export default function ProformaActivity({ proformaId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    if (!proformaId) return;
    const token = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const feed = await proformaApi.activity(proformaId);
      if (token !== reqRef.current) return;
      setEntries(normalizeEntries(feed));
    } catch (e) {
      if (token === reqRef.current) setError(backendError(e, 'Failed to load activity.'));
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [proformaId]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const created = await proformaApi.addComment(proformaId, body);
      // Append the returned entry (keeps oldest→newest order); clear the box.
      setEntries((prev) => normalizeEntries({ entries: [...prev, created] }));
      setDraft('');
    } catch (e) {
      message.error(backendError(e, 'Could not post the comment.'));
    } finally {
      setPosting(false);
    }
  }, [draft, posting, proformaId]);

  // Ctrl/Cmd+Enter sends (Enter alone keeps newlines for multi-line notes).
  const onKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
  }, [submit]);

  // Newest-first for the log (most recent activity on top).
  const ordered = entries.slice().reverse();

  return (
    <div className="pf-act">
      {/* Composer — append an immutable note. */}
      <div className="pf-act-composer">
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add a note or update… (Ctrl+Enter to post)"
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={4000}
          disabled={posting}
          aria-label="Add a comment"
        />
        <div className="pf-act-composer-actions">
          <span className="pf-act-hint">Comments are permanent and visible to your team.</span>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            loading={posting}
            disabled={!draft.trim()}
            onClick={submit}
          >
            Post
          </Button>
        </div>
      </div>

      {/* Timeline. */}
      {loading && entries.length === 0 ? (
        <div className="pf-act-state"><Spin /><span>Loading activity…</span></div>
      ) : error ? (
        <div className="pf-act-state">
          <span className="pf-act-error">{error}</span>
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>Retry</Button>
        </div>
      ) : ordered.length === 0 ? (
        <div className="pf-act-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity yet — start the conversation." />
        </div>
      ) : (
        <ul className="pf-act-list">
          {ordered.map((e) => <ActivityItem key={`${e.kind}:${e.id}`} entry={e} />)}
        </ul>
      )}
    </div>
  );
}
